import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { redirectOrPageReload } from '../../utils/handleRedirects';
import { SigningModeType } from '../../shared/constants/signingModeType';
import { SIGNING_FLOW_TYPE_MAP } from '../../shared/constants/signingFlowType';
import { getSigningFlowType } from '../../utils/getSigningFlowType';
import {
    MIXPANEL_SIGNING_MODE_TYPE_MAP,
    MixpanelSigningFlowType,
    MixpanelSigningModeType,
} from '../../events/mixpanel/mixpanel.constants';
import { globalMixpanelReporter } from '../../events/mixpanel/mixpanel.reporter';
import { globalBigQueryReporter } from '../../events/bigQuery/bigQuery.reporter';
import { globalGrafanaReporter } from '../../events/grafana/grafana.reporter';
import { ContextProps, IdentificationContextProviderProps } from './IdentificationContext.types';

export const IdentificationContext = React.createContext<ContextProps | undefined>(undefined);

export const IdentificationContextProvider = ({
    children,
    initialMode,
    initialEmail,
    initialUsername,
    isModal,
    isModalOpen,
    isSticky,
    isSocialFlow: initialIsSocialFlow = false,
    skipActivation: initialSkipActivation = false,
    closeModal,
    skipQuestionnaire,
    variant = 'default',
    redirectUrl = '',
    source,
    blockedActionType,
    afterSigningSuccess,
}: IdentificationContextProviderProps) => {
    const [mode, setMode] = useState<SigningModeType>(initialMode);
    const [isSocialFlow, setIsSocialFlow] = useState(initialIsSocialFlow);
    const [skipActivation, setSkipActivation] = useState(initialSkipActivation);
    const [email, setEmail] = useState<string>(initialEmail || '');
    const [username, setUserName] = useState<string>(initialUsername || '');
    const [password, setPassword] = useState<string>();
    const [shouldNavigateOnClose, setShouldNavigateOnClose] = useState(false);
    const redirectUrlRef = useRef<string>(redirectUrl);

    const setRedirectUrl = useCallback((url: string) => {
        redirectUrlRef.current = url;
        globalBigQueryReporter.enrichContext({ redirectUrl: url });
        globalMixpanelReporter.enrichContext({ redirectUrl: url });
    }, []);

    const navigateToRedirectUrl = useCallback(() => {
        redirectOrPageReload(redirectUrlRef.current);
    }, []);

    const finishSigningFlowAndNavigate = useCallback(() => {
        globalGrafanaReporter.count('identification.%{platform}.completed');
        globalMixpanelReporter.send('Identification - Completed');
        globalBigQueryReporter.send('identification.completed');

        if (afterSigningSuccess) {
            afterSigningSuccess({
                redirectUrl: redirectUrlRef.current,
                signingFlowType: getSigningFlowType({
                    mode,
                    isSocialFlow,
                }),
            });
            return;
        }

        navigateToRedirectUrl();
    }, [afterSigningSuccess, navigateToRedirectUrl, mode, isSocialFlow]);

    const handleSetMode = useCallback((mode: SigningModeType) => {
        setMode(mode);
        const bqEnrichment = {
            mode,
            flow: mode === 'sign_up' ? SIGNING_FLOW_TYPE_MAP.signUp : SIGNING_FLOW_TYPE_MAP.signIn,
        };
        globalBigQueryReporter.enrichContext(bqEnrichment);

        const mixpanelEnrichment = {
            mode: MIXPANEL_SIGNING_MODE_TYPE_MAP[mode],
            flow: mode === 'sign_up' ? 'Sign Up' : 'Sign In',
        } satisfies { mode: MixpanelSigningModeType; flow: MixpanelSigningFlowType };
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
    }, []);

    useEffect(() => {
        if (isModal && !isModalOpen && shouldNavigateOnClose) {
            finishSigningFlowAndNavigate();
        }
    }, [isModal, isModalOpen, shouldNavigateOnClose, finishSigningFlowAndNavigate]);

    useEffect(() => {
        globalBigQueryReporter.enrichContext({ redirectUrl });
        globalMixpanelReporter.enrichContext({ redirectUrl });
    }, [redirectUrl]);

    const contextValue = useMemo(
        () => ({
            mode,
            email,
            username,
            variant,
            isModal,
            closeModal,
            setEmail,
            setUserName,
            setMode: handleSetMode,
            finishSigningFlowAndNavigate,
            isSticky,
            isSocialFlow,
            setIsSocialFlow,
            skipQuestionnaire,
            blockedActionType,
            password,
            setPassword,
            setRedirectUrl,
            skipActivation,
            setSkipActivation,
            source,
            navigateToRedirectUrl,
            enableShouldNavigateOnClose: () => setShouldNavigateOnClose(true),
        }),
        [
            mode,
            email,
            username,
            variant,
            isModal,
            isSocialFlow,
            closeModal,
            handleSetMode,
            finishSigningFlowAndNavigate,
            isSticky,
            skipQuestionnaire,
            blockedActionType,
            password,
            setRedirectUrl,
            skipActivation,
            source,
            navigateToRedirectUrl,
        ]
    );

    return <IdentificationContext.Provider value={contextValue}>{children}</IdentificationContext.Provider>;
};

export const useIdentificationContext = () => {
    const value = useContext(IdentificationContext);

    if (value === undefined) {
        throw new Error('useIdentificationContext must be used within a IdentificationContext.Provider');
    }

    return value;
};
